import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FiX, FiTrash2, FiAlertCircle, FiClock } from "react-icons/fi";
import { HiUserGroup, HiOutlineMail } from "react-icons/hi";
import { FaRegUserCircle } from "react-icons/fa";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalHeaderIcon,
  ModalHeaderContent,
  ModalHeaderTextWrapper,
  ModalSubtitle,
  InviteSection,
  InviteSectionHeader,
  InviteSectionLabel,
  MemberLimitIndicator,
  MemberLimitText,
  MemberLimitBar,
  MemberLimitProgress,
  LimitWarning,
  LimitWarningContent,
  LimitWarningTitle,
  LimitWarningText,
  UpgradeButton,
  InviteFormFields,
  InputWrapper,
  InputIcon,
  StyledInput,
  InviteFormActions,
  StyledSelect,
  InviteButton,
  MembersListSection,
  MembersListHeader,
  MembersList,
  MemberCard,
  MemberAvatar,
  MemberInfo,
  MemberNameRow,
  MemberName,
  MemberBadge,
  MemberEmail,
  MemberActions,
  MemberRoleLabel,
  MemberRoleSelect,
  DeleteButton,
  ConfirmModalFooter,
  CancelButton,
  ConfirmButton,
  ConfirmModalText,
} from "./Styles";
import { useBusiness } from "../../Common/Hooks/useBusiness";
import { useAuth } from "../../Common/Hooks/useAuth";
import { MemberRol } from "../../Common/Types-DTOs/ClientUser";
import { TeamMember } from "../../Common/Types-DTOs/TeamMember";
import { isErr } from "../../Common/Utils/Result";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";
import { emailRegex } from "../../Common/Utils/Validators";
import { getAvatarColor } from "../../Common/Utils/getAvatarColor";
import { isAdmin } from "../../Common/Utils/isAdmin";
import { CustomPhoneInput } from "../CustomPhoneInput";
import { getErrorMessage } from "../../Common/Utils/errorMessages";

interface UsersModalProps {
  onClose: () => void;
  onOpenSubscription?: () => void;
}


export const UsersModal = ({ onClose, onOpenSubscription }: UsersModalProps) => {
  const { t } = useTranslation();
  const { business, invites, getInvites, removeUser, delInvite, inviteUser, updateMemberRole } = useBusiness();
  const { clientUser: client } = useAuth();
  const userIsAdmin = isAdmin(business, client);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteRole, setInviteRole] = useState(MemberRol.AGENT);
  const [isInviting, setIsInviting] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    if (business?.id) {
      getInvites();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const allMembers: TeamMember[] = (() => {
    const members: TeamMember[] = [];

    if (business?.members) {
      business.members.forEach((member) => {
        const name = member.client?.name || member.invitedMemberDataPreActivation?.name || t("common.sidebar.userMenu.fallbackName");
        const email = member.client?.email || member.invitedMemberDataPreActivation?.email || "";

        members.push({
          id: member.memberId,
          memberId: member.memberId,
          name,
          email,
          role: member.rol,
          avatar: name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase()
            .slice(0, 2),
          isCurrentUser: member.client?.id === client?.id,
          isPending: false,
          isInvite: false,
        });
      });
    }

    if (invites) {
      invites.forEach((invite) => {
        const name = invite.name || t("common.sidebar.userMenu.fallbackName");
        members.push({
          id: invite.id,
          name,
          email: invite.email || "",
          role: invite.rol || MemberRol.AGENT,
          avatar: name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase()
            .slice(0, 2),
          isCurrentUser: false,
          isPending: true,
          isInvite: true,
          inviteId: invite.id,
        });
      });
    }

    return members;
  })();

  const currentMembersCount = business?.members?.length || 0;
  const pendingInvitesCount = invites?.length || 0;
  const totalMembers = currentMembersCount + pendingInvitesCount;
  const memberLimit = business?.memberAllowedQuantity || 0;
  const hasReachedLimit = totalMembers >= memberLimit;
  const limitPercentage = memberLimit > 0 ? (totalMembers / memberLimit) * 100 : 0;

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      if (memberToDelete.isInvite && memberToDelete.inviteId) {
        const result = await delInvite(memberToDelete.inviteId);
        if (isErr(result)) {
          eventBus.emit(
            "NOTIFICATION",
            Notification.buildNotification("error", t("common.sidebar.usersModal.errors.cancelInviteFailed"))
          );
          return;
        }
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("success", t("common.sidebar.usersModal.success.inviteCancelled"))
        );
      } else if (memberToDelete.memberId) {
        const result = await removeUser(memberToDelete.memberId);
        if (isErr(result)) {
          eventBus.emit(
            "NOTIFICATION",
            Notification.buildNotification("error", t("common.sidebar.usersModal.errors.removeMemberFailed"))
          );
          return;
        }
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("success", t("common.sidebar.usersModal.success.memberRemoved"))
        );
      }
    } catch (error) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.sidebar.usersModal.errors.unexpectedError"))
      );
    }
    setMemberToDelete(null);
  };

  const handleInviteMember = async () => {
    if (hasReachedLimit) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("common.sidebar.usersModal.errors.limitReached")
        )
      );
      return;
    }

    if (!inviteEmail || !inviteName || !invitePhone) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.sidebar.usersModal.errors.missingFields"))
      );
      return;
    }

    if (!emailRegex.test(inviteEmail)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.sidebar.usersModal.errors.invalidEmail"))
      );
      return;
    }

    setIsInviting(true);
    const result = await inviteUser({
      email: inviteEmail,
      phone: invitePhone,
      name: inviteName,
      rol: inviteRole
    });
    setIsInviting(false);

    if (isErr(result)) {
      const error = result.error;
      const errorCode = error && typeof error === 'object' && 'errorCode' in error && typeof error.errorCode === 'string'
        ? error.errorCode
        : undefined;
      const errorMessage = getErrorMessage(errorCode);

      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", errorMessage)
      );
      return;
    }

    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification("success", t("common.sidebar.usersModal.success.inviteSent"))
    );

    setInviteEmail("");
    setInviteName("");
    setInvitePhone("");
    setInviteRole(MemberRol.AGENT);

    if (business) {
      getInvites();
    }
  };

  const handleRoleChange = async (member: TeamMember, newRole: MemberRol) => {
    if (!member.memberId) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.sidebar.usersModal.errors.roleChangePendingInvite"))
      );
      return;
    }

    const result = await updateMemberRole(member.memberId, newRole);

    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.sidebar.usersModal.errors.roleChangeFailed"))
      );
      return;
    }

    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification("success", t("common.sidebar.usersModal.success.roleUpdated"))
    );

    if (business) {
      getInvites();
    }
  };

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()} style={{ maxWidth: "640px" }}>
        <ModalHeader style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: "1rem" }}>
          <ModalHeaderContent>
            <ModalHeaderIcon style={{ backgroundColor: "#dbeafe" }}>
              <HiUserGroup size={20} color="#3b82f6" />
            </ModalHeaderIcon>
            <ModalHeaderTextWrapper>
              <ModalTitle style={{ marginBottom: "0.25rem" }}>{t("common.sidebar.usersModal.title")}</ModalTitle>
              <ModalSubtitle>
                {t("common.sidebar.usersModal.subtitle")}
              </ModalSubtitle>
            </ModalHeaderTextWrapper>
          </ModalHeaderContent>
          <ModalCloseButton onClick={onClose}>
            <FiX size={20} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          {userIsAdmin && (
          <InviteSection>
            <InviteSectionHeader>
              <InviteSectionLabel>
                {t("common.sidebar.usersModal.inviteSection.label")}
              </InviteSectionLabel>
              <MemberLimitIndicator>
                <MemberLimitText>
                  {t("common.sidebar.usersModal.inviteSection.memberCount", { count: totalMembers, limit: memberLimit })}
                </MemberLimitText>
                <MemberLimitBar>
                  <MemberLimitProgress
                    $percentage={limitPercentage}
                    $hasReachedLimit={hasReachedLimit}
                  />
                </MemberLimitBar>
              </MemberLimitIndicator>
            </InviteSectionHeader>

            {hasReachedLimit && (
              <LimitWarning>
                <FiAlertCircle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: "0.125rem" }} />
                <LimitWarningContent>
                  <LimitWarningTitle>
                    {t("common.sidebar.usersModal.inviteSection.limitWarning.title")}
                  </LimitWarningTitle>
                  <LimitWarningText>
                    {t("common.sidebar.usersModal.inviteSection.limitWarning.text", { count: memberLimit })}
                  </LimitWarningText>
                  <UpgradeButton
                    onClick={() => {
                      if (onOpenSubscription) {
                        onOpenSubscription();
                      }
                    }}
                  >
                    {t("common.sidebar.usersModal.inviteSection.limitWarning.upgradeButton")}
                  </UpgradeButton>
                </LimitWarningContent>
              </LimitWarning>
            )}

            {!hasReachedLimit && (
              <InviteFormFields>
                <InputWrapper>
                  <InputIcon>
                    <FaRegUserCircle size={18} />
                  </InputIcon>
                  <StyledInput
                    type="text"
                    placeholder={t("common.sidebar.usersModal.inviteSection.namePlaceholder")}
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                  />
                </InputWrapper>

                <InputWrapper>
                  <InputIcon>
                    <HiOutlineMail size={18} />
                  </InputIcon>
                  <StyledInput
                    type="email"
                    placeholder={t("common.sidebar.usersModal.inviteSection.emailPlaceholder")}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </InputWrapper>

                <div>
                  <CustomPhoneInput
                    placeholder={t("common.sidebar.usersModal.inviteSection.phonePlaceholder")}
                    value={invitePhone}
                    onChange={(phoneNumber) => setInvitePhone(phoneNumber)}
                  />
                </div>

                <InviteFormActions>
                  <StyledSelect
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as MemberRol)}
                  >
                    <option value={MemberRol.AGENT}>{t("common.sidebar.usersModal.inviteSection.roleOptions.member")}</option>
                    <option value={MemberRol.ADMINISTRATIVE}>{t("common.sidebar.usersModal.inviteSection.roleOptions.admin")}</option>
                  </StyledSelect>
                  <InviteButton
                    onClick={handleInviteMember}
                    disabled={isInviting}
                    $isLoading={isInviting}
                  >
                    {isInviting ? (
                      t("common.sidebar.usersModal.inviteSection.inviteButtonLoading")
                    ) : (
                      <>
                        <HiOutlineMail size={16} />
                        {t("common.sidebar.usersModal.inviteSection.inviteButton")}
                      </>
                    )}
                  </InviteButton>
                </InviteFormActions>
              </InviteFormFields>
            )}
          </InviteSection>
          )}

          <MembersListSection>
            <MembersListHeader>
              {t("common.sidebar.usersModal.membersList.header", { count: totalMembers, limit: memberLimit })}
            </MembersListHeader>
            <MembersList>
              {allMembers.map((member) => {
                const avatarColor = getAvatarColor(member.email);
                const canDelete = !member.isCurrentUser && member.role !== MemberRol.OWNER;

                return (
                  <MemberCard key={member.id}>
                    <MemberAvatar
                      $bgColor={avatarColor.bg}
                      $textColor={avatarColor.text}
                    >
                      {member.avatar}
                    </MemberAvatar>
                    <MemberInfo>
                      <MemberNameRow>
                        <MemberName>
                          {member.name}
                        </MemberName>
                        {member.isCurrentUser && (
                          <MemberBadge $type="current">
                            {t("common.sidebar.usersModal.membersList.currentUserBadge")}
                          </MemberBadge>
                        )}
                        {member.isPending && (
                          <MemberBadge $type="pending">
                            <FiClock size={12} />
                            {t("common.sidebar.usersModal.membersList.pendingBadge")}
                          </MemberBadge>
                        )}
                      </MemberNameRow>
                      <MemberEmail>
                        {member.email}
                      </MemberEmail>
                    </MemberInfo>
                    <MemberActions>
                      {member.role === MemberRol.OWNER ? (
                        <MemberRoleLabel>
                          {t("common.sidebar.usersModal.membersList.ownerLabel")}
                        </MemberRoleLabel>
                      ) : (
                        <MemberRoleSelect
                          value={member.role}
                          onChange={(e) => handleRoleChange(member, e.target.value as MemberRol)}
                          disabled={member.isCurrentUser || member.isPending}
                          $disabled={member.isCurrentUser || member.isPending}
                        >
                          <option value={MemberRol.ADMINISTRATIVE}>{t("common.sidebar.usersModal.membersList.roleOptions.admin")}</option>
                          <option value={MemberRol.AGENT}>{t("common.sidebar.usersModal.membersList.roleOptions.member")}</option>
                          <option value={MemberRol.DEVELOPER}>{t("common.sidebar.usersModal.membersList.roleOptions.developer")}</option>
                        </MemberRoleSelect>
                      )}
                      {canDelete && userIsAdmin && (
                        <DeleteButton
                          onClick={() => setMemberToDelete(member)}
                          title={member.isPending ? t("common.sidebar.usersModal.membersList.deleteButton.cancelInviteTitle") : t("common.sidebar.usersModal.membersList.deleteButton.removeMemberTitle")}
                        >
                          <FiTrash2 size={18} />
                        </DeleteButton>
                      )}
                    </MemberActions>
                  </MemberCard>
                );
              })}
            </MembersList>
          </MembersListSection>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>

    {memberToDelete && (
      <ModalOverlay onClick={() => setMemberToDelete(null)}>
        <ModalContainer onClick={(e) => e.stopPropagation()} style={{ maxWidth: "480px" }}>
          <ModalHeader>
            <ModalHeaderContent>
              <ModalHeaderIcon style={{ backgroundColor: "#fee2e2" }}>
                <FiAlertCircle size={20} color="#dc2626" />
              </ModalHeaderIcon>
              <ModalTitle>{t("common.sidebar.usersModal.confirmDelete.title")}</ModalTitle>
            </ModalHeaderContent>
            <ModalCloseButton onClick={() => setMemberToDelete(null)}>
              <FiX size={20} />
            </ModalCloseButton>
          </ModalHeader>

          <ModalBody>
            <ConfirmModalText>
              {memberToDelete.isPending ? (
                <Trans
                  i18nKey="common.sidebar.usersModal.confirmDelete.pendingText"
                  values={{ name: memberToDelete.name }}
                  components={{ 1: <strong /> }}
                />
              ) : (
                <Trans
                  i18nKey="common.sidebar.usersModal.confirmDelete.memberText"
                  values={{ name: memberToDelete.name }}
                  components={{ 1: <strong /> }}
                />
              )}
            </ConfirmModalText>
          </ModalBody>

          <ConfirmModalFooter>
            <CancelButton onClick={() => setMemberToDelete(null)}>
              {t("common.sidebar.usersModal.confirmDelete.cancelButton")}
            </CancelButton>
            <ConfirmButton onClick={handleConfirmDelete}>
              {memberToDelete.isPending
                ? t("common.sidebar.usersModal.confirmDelete.confirmCancelInvite")
                : t("common.sidebar.usersModal.confirmDelete.confirmRemoveMember")}
            </ConfirmButton>
          </ConfirmModalFooter>
        </ModalContainer>
      </ModalOverlay>
    )}
  </>
  );
};




